import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, InjectionToken, computed, inject } from '@angular/core';

import moment from 'moment';
import { Observable, map } from 'rxjs';
import * as z from 'zod';

import { Addon, Agreement } from '../schemas';
import { storedResource } from '../shared';
import { Lambda } from './lambda';
import { Messenger } from './messenger';

const EmptyObject = z.strictObject({});

const PreparedDocumentInfo = z.strictObject({
  key: z.string(),
  url: z.string(),
});

export const LCM_EXPIRING_WINDOW = new InjectionToken<number>("Number of days till expiration required for expiring agreement notification", {factory: () => 90})

const Response = z.strictObject({
  Count: z.number(),
  ScannedCount: z.number(),
  Items: z.array(Agreement),
});

@Injectable({ providedIn: 'root' })
export class AgreementService {
  private http = inject(HttpClient);
  public readonly expiringWindow = inject(LCM_EXPIRING_WINDOW);

  private url = inject(Lambda).getAgreementsUrl();
  private documentsUrl = inject(Lambda).getDocumentsUrl();

  private readonly CACHE_KEY = `lcm:service-cache:agreement`;

  private agreementsResource = httpResource<z.infer<typeof Response>>(() => this.url, {
    defaultValue: JSON.parse(window.localStorage.getItem(this.CACHE_KEY) || '{"Items": []}'),
    parse: Response.parse,
  });

  constructor() {
    storedResource(this.agreementsResource, this.CACHE_KEY, inject(Messenger));
  }

  agreements = computed(() => this.agreementsResource.value().Items);
  pending = this.agreementsResource.isLoading;

  expiring = computed(() => {
    const agreements = this.agreements();
    const now = new Date();

    return agreements.filter((agreement: Agreement) => {
      if (!['Active', 'Pending'].includes(agreement.Status)) {
        return false;
      }

      const expiring = new Date(agreement.EndDate).getTime() - now.getTime();
      const expiringIn = Math.ceil(expiring / 1000 / 3600 / 24);

      agreement['_expiringIn'] = expiringIn;
      return expiringIn < this.expiringWindow;
    });
  });

  separateAddons = computed(() => {
    const supportedStatuses = ['Active'];
    const result: (Addon & { parent: Agreement })[] = [];

    this.agreements().forEach((agreement) => {
      if (!supportedStatuses.includes(agreement.Status)) {
        return;
      }
      agreement.Addons.forEach((addon) => {
        const now = new Date();
        const invoiceDate = addon.NextInvoiceDate ? new Date(addon.NextInvoiceDate) : now;
        const diff = invoiceDate.getTime() - now.getTime();

        const invoiceIn = Math.ceil(diff / 1000 / 3600 / 24);

        if (!addon.InvoiceSeparately || invoiceIn > 14) {
          return;
        }

        result.push({
          ...addon,
          parent: agreement,
        });
        addon['parent'] = agreement;
        addon['_invoiceIn'] = invoiceIn;
      });
    });

    return result.sort(({ NextInvoiceDate: left }, { NextInvoiceDate: right }) =>
      left < right ? -1 : left > right ? 1 : 0,
    );
  });

  reload() {
    this.agreementsResource.reload();
  }

  save(instance: Agreement, forEdit = false) {
    const data = Agreement.strip()
      .omit({
        Addons: true,
        UnchargeableHours: true,
        ProfitableHours: true,
        UnforeseenHours: true,
        PurchasedHours: true,
      })
      .extend({
        Documents: z.transform((v) => JSON.stringify(v)),
        StartDate: z.transform((v) => (moment.isMoment(v) ? v.format('Y-MM-DD') : v)),
        EndDate: z.transform((v) => (moment.isMoment(v) ? v.format('Y-MM-DD') : v)),
      })
      .parse(instance);

    let request: Observable<Agreement | Record<string, never>>;
    if (forEdit) {
      request = this.http
        .put<Record<string, never>>(this.url + '/' + data.AgreementNumber, data)
        .pipe(map((data) => EmptyObject.parse(data)));
    } else {
      request = this.http.post<Agreement>(this.url, data).pipe(map((data) => Agreement.parse(data)));
    }

    return request;
  }

  downloadDocument(id: string): void {
    this.http
      .get(this.documentsUrl + '/download/single/' + id, {
        observe: 'body',
        responseType: 'text',
      })
      .subscribe((url: string) => {
        window.open(url, '_blank');
      });
  }

  prepareDocumentUpload(name: string, type: string) {
    return this.http
      .post(this.documentsUrl, { ContentType: type, DocumentName: name })
      .pipe(map((data) => PreparedDocumentInfo.parse(data)));
  }

  updateNextInvoiceDate(agreementNumber: string, addonId?: string) {
    return this.http
      .put(this.url + '/' + agreementNumber + '/update-invoice-date', { addonId })
      .pipe(map((data) => Agreement.parse(data)));
  }

  setNextInvoiceDate(agreementNumber: string, date: string) {
    return this.http
      .put(`${this.url}/${agreementNumber}/set-invoice-date/${date}`, {})
      .pipe(map((data) => Agreement.parse(data)));
  }

  addHours(id: string, data: object, type: string) {
    const url = this.url + '/' + id + '/hours/' + type;
    return this.http.put(url, data).pipe(map((data) => EmptyObject.parse(data)));
  }

  updateHours(id: string, data: object, type: string) {
    const url = this.url + '/' + id + '/update-hours/' + type;
    return this.http.put(url, data).pipe(map((data) => EmptyObject.parse(data)));
  }

  removeHours(id: string, type: string, uuid: string | number) {
    const url = this.url + '/' + id + '/hours/' + type + '/' + uuid;
    return this.http.delete(url).pipe(map((data) => EmptyObject.parse(data)));
  }

  addAddon(id: string, data: object) {
    const url = this.url + '/' + id + '/addons';
    return this.http.put(url, data).pipe(map((data) => EmptyObject.parse(data)));
  }

  removeAddon(id: string, uuid: string | number) {
    const url = this.url + '/' + id + '/addons' + '/' + uuid;
    return this.http.delete(url).pipe(map((data) => EmptyObject.parse(data)));
  }
}
